<template>
  <div v-if="isGuardPassed" class="success-page" :style="pageStyle">
    <SuccessCheckmark />

    <h2 class="success-title" :style="titleStyle">
      {{ successTitle }}
    </h2>

    <p class="success-text" :style="textStyle">
      {{ successText }}
    </p>

    <component
      :is="isExternalUrl ? 'a' : resolveComponent('NuxtLink')"
      class="success-button"
      :class="{ 'success-button--round': isRoundCorners, 'success-button--square': isSquareCorners }"
      :style="buttonStyle"
      v-bind="buttonLinkProps"
      @click="onButtonClick"
    >
      {{ buttonText }}
    </component>
  </div>
</template>

<script setup lang="ts">
import { colorValue } from "@jobshop/crossroads/components/Forms/formColorDefaults.js"

useHead({ meta: [{ name: "robots", content: "noindex" }] })

const route = useRoute()
const customPath = useCustomPath()

// Starts hidden — content only shown after the guard passes.
// This prevents the SSR-rendered page from flashing before onMounted fires.
const isGuardPassed = ref(false)

// Access guard: only accessible after a successful submission.
// Must run on the client (sessionStorage is unavailable during SSR).
onMounted(async () => {
  if (!sessionStorage.getItem("applicationSubmitted")) {
    await navigateTo(customPath.value || "/", { replace: true })
    return
  }
  sessionStorage.removeItem("applicationSubmitted")
  isGuardPassed.value = true
})

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function toValidUuid(value: unknown): string | null {
  const s = String(value ?? "")
  return UUID_RE.test(s) ? s : null
}

// Read the form config that was already fetched and cached by CRFormRendererContainer
// on the job page — reusing the cache avoids a second API call and any risk of 404.
// The cache key matches useFormConfig's useAsyncData key exactly.
const jobId = toValidUuid(route.query.jobId)
const { data: cachedFormConfig } = useNuxtData<FormConfig>(jobId ? `formConfig-${jobId}` : "__none__")

const form = computed(() => cachedFormConfig.value?.form ?? null)
const successPage = computed(() => form.value?.successPage ?? null)

// Text content from success page config with fallbacks
const successTitle = computed(() => successPage.value?.title ?? "Application Successful!")
const successText = computed(
  () =>
    successPage.value?.text
    ?? "Congratulations! Your application has been successfully submitted. We appreciate your interest and will be in touch soon.",
)

const titleStyle = computed(() => {
  const color = colorValue(successPage.value?.titleColor)
  return color ? { color } : {}
})

const textStyle = computed(() => {
  const color = colorValue(successPage.value?.textColor)
  return color ? { color } : {}
})

const pageStyle = computed(() => {
  const bg = colorValue(successPage.value?.backgroundColor)
  return bg ? { backgroundColor: bg } : {}
})

// Button config from success page settings
const successButton = computed(() => successPage.value?.button ?? null)
const buttonText = computed(() => successButton.value?.text ?? "Back to job search")
const buttonUrl = computed(() => successButton.value?.url ?? `${customPath.value ?? ""}`)

// Block dangerous URI schemes before any other URL handling
const isSafeUrl = computed(() => !/^(javascript|data|vbscript):/i.test(buttonUrl.value.trim()))

// Detect absolute URL (http:// or https:// or protocol-relative //)
const isExternalUrl = computed(() => /^(https?:)?\/\//.test(buttonUrl.value))

const buttonLinkProps = computed(() => {
  if (!isSafeUrl.value)
    return { to: customPath.value || "/" }

  if (isExternalUrl.value)
    return { href: buttonUrl.value }

  return { to: buttonUrl.value }
})

// Button styling — mirrors FormRenderer applyButton logic
const isRoundCorners = computed(() => successButton.value?.corners?.value === "round")
const isSquareCorners = computed(() => successButton.value?.corners?.value === "square")

const buttonStyle = computed(() => {
  const btn = successButton.value
  if (!btn)
    return {}

  const bg = colorValue(btn.backgroundColor) || undefined
  const text = colorValue(btn.textColor) || undefined
  const fill = btn.fillStyle?.value

  if (fill === "outline") {
    return {
      border: `1px solid ${bg ?? "#ccc"}`,
      color: text ?? bg ?? "inherit",
      background: "transparent",
    }
  }

  return {
    background: bg ?? "#fff",
    border: `1px solid ${bg ?? "#ccc"}`,
    color: text ?? "inherit",
  }
})

function onButtonClick() {
  trackSuccessButtonClick(buttonUrl.value)
}

// Page tracking
usePageTracking()
</script>

<style scoped lang="scss">
.success-page {
  display: flex;
  flex-direction: column;
  gap: 25px;
  align-items: center;
  justify-content: center;

  min-height: 70vh;
  padding: 64px 128px;

  text-align: center;

  background: #fff;

  @media (width <= 1600px) {
    padding: 64px;
  }

  @media (width <= 1033px) {
    padding: 48px 32px;
  }

  @media (width <= 733px) {
    padding: 48px 25px;
  }
}

.success-title {
  max-width: 1472px;

  font-size: 46px;
  font-weight: 600;
  line-height: 1.17;
  color: #1D1D1F;
  letter-spacing: -0.03em;

  @media (width <= 1033px) {
    font-size: 40px;
  }

  @media (width <= 733px) {
    width: 100%;
    font-size: 32px;
    letter-spacing: -0.0625em;
  }
}

.success-text {
  max-width: 1472px;

  font-size: 17px;
  font-weight: 600;
  line-height: 1.59;
  color: #7D7D7D;

  @media (width <= 733px) {
    width: 100%;
    max-width: 100%;
  }
}

.success-button {
  cursor: pointer;

  display: inline-flex;
  gap: 5px;
  align-items: center;
  justify-content: center;

  height: 54px;
  padding: 0 30px;

  font-size: 17px;
  font-weight: 500;
  color: #111;
  text-decoration: none;
  letter-spacing: 0.01em;
  white-space: nowrap;

  background: transparent;
  border: 2px solid #E2E2E2;
  border-radius: 40px;

  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.75;
  }

  &--round {
    border-radius: 1000px;
  }

  &--square {
    border-radius: 0;
  }
}
</style>
